import axios from "axios";
import _ from "lodash";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { setNotSyncing, setSyncDate, setSyncing } from "./slices/syncSlice";
import { syncPropertiesTranslations } from "./syncPropertiesTranslations";

const statuses = {
  UpToDate: "upToDate",
  Updated: "updated",
  Outdated: "outdated",
} as const;

export function createSyncData(data: any, syncProperties: string[]) {
  return syncProperties.reduce((acc, key) => {
    acc[key] = data[key];
    return acc;
  }, {});
}

export async function useSyncRedux({
  property,
  syncProperties,
  selector,
  interval = 30000,
  setDataReducer,
  patchImagesReducer,
  afterSync = () => {},
}: {
  property: string;
  syncProperties: string[];
  selector: (state: any) => any;
  interval?: number;
  setDataReducer: any;
  patchImagesReducer: any;
  afterSync?: () => void;
}) {
  const dispatch = useDispatch();

  const data = useSelector(selector);

  const user = useSelector((state: any) => state.user);

  const revision = data.revision;

  const [forceSync, setForceSync] = useState(false);

  const syncState = useSelector((state: any) => state.sync);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setForceSync((forceSync) => !forceSync);
    }, interval);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (syncState.syncing.includes(property)) return;
      dispatch(setSyncing({ property }));
      const response = await sync({
        revision: revision,
        userToken: user.token,
        property: property,
        data: createSyncData(data, syncProperties),
      }).catch(() => {});

      dispatch(setNotSyncing({ property }));

      if (!response) return;

      if ("newData" in response) {
        if (response.revision > (revision ?? -1))
          dispatch(
            setDataReducer({
              revision: response.revision,
              newData: response.newData,
            })
          );
        dispatch(
          setSyncDate({
            property,
            date: new Date(),
            revision: response.revision,
          })
        );
        return;
      }

      dispatch(patchImagesReducer(response.imagesToReplace));

      dispatch(setSyncDate({ property, date: new Date(), revision: revision }));

      afterSync();
    }, 1000);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision, forceSync, user?.token]);
}

export default async function sync({
  revision,
  property,
  data,
  userToken,
}: {
  revision: number;
  property: string;
  data: any;
  userToken: string;
}): Promise<
  | { revision: number; imagesToReplace: { [key: string]: any } }
  | { newData: any; revision: number }
> {
  const response = await axios
    .post(`${process.env.REACT_APP_API_URL}/sync/` + property, {
      revision,
      data,
      token: userToken,
    })
    .catch(() => {});

  if (!response || !response?.data) {
    throw new Error("Failed to sync");
  }

  if (response.data.status === statuses.UpToDate)
    return {
      revision: revision,
      imagesToReplace: response.data.imagesToReplace,
    };

  if (response.data.status === statuses.Updated)
    return {
      revision: response.data.revision,
      imagesToReplace: response.data.imagesToReplace,
    };

  if (response.data.status === statuses.Outdated)
    return { newData: response.data.data, revision: response.data.revision };
}

export const syncSetDataReducer =
  (syncProperties: string[], property: string) => (state, action) => {
    const { revision, newData } = action.payload;
    if (!_.isNull(revision) && state.revision > revision) return;
    if (_.isNull(state.revision)) {
      toast(`Úspěšně přeneseno ${syncPropertiesTranslations[property]}`);
    }
    state.revision = revision;
    for (const property of syncProperties) {
      state[property] = newData[property];
    }
  };

export const syncPatchImagesReducer = (state, action) => {
  const patch = action.payload;
  _.merge(state, patch);
};

export const useGetRevisions = () => {
  const userState = useSelector((state: any) => state.user);
  const alcoholState = useSelector((state: any) => state.alcohol);
  const ownItemsState = useSelector((state: any) => state.ownItems);

  return {
    "user-settings": userState.revision,
    "alcohol-data": alcoholState.revision,
    "own-items": ownItemsState.revision,
  };
};
